import { types } from "../types/types"

const TICKET_TABS_STORAGE_KEY = 'bradev.ticketTabs';

const loadPersistedTicketTabs = () => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return {
            openTicketTabs: [],
            activeTicketTabId: '',
            companyId: '',
            userId: '',
        };
    }

    try {
        const raw = window.localStorage.getItem(TICKET_TABS_STORAGE_KEY);
        if (!raw) {
            return {
                openTicketTabs: [],
                activeTicketTabId: '',
                companyId: '',
                userId: '',
            };
        }

        const parsed = JSON.parse(raw);
        const openTicketTabs = Array.isArray(parsed?.openTicketTabs)
            ? parsed.openTicketTabs
                .map((tab) => ({
                    id: String(tab?.id || '').trim(),
                    code: String(tab?.code || '').trim(),
                    title: String(tab?.title || '').trim(),
                }))
                .filter((tab) => Boolean(tab.id))
            : [];

        const activeTicketTabId = String(parsed?.activeTicketTabId || '').trim();
        const activeExists = openTicketTabs.some((tab) => tab.id === activeTicketTabId);

        return {
            openTicketTabs,
            activeTicketTabId: activeExists ? activeTicketTabId : '',
            companyId: String(parsed?.companyId || '').trim(),
            userId: String(parsed?.userId || '').trim(),
        };
    } catch (error) {
        return {
            openTicketTabs: [],
            activeTicketTabId: '',
            companyId: '',
            userId: '',
        };
    }
};

const persistTicketTabs = (openTicketTabs, activeTicketTabId, companyId, userId) => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return;
    }

    try {
        window.localStorage.setItem(TICKET_TABS_STORAGE_KEY, JSON.stringify({
            openTicketTabs,
            activeTicketTabId,
            companyId,
            userId,
        }));
    } catch (error) {
        // no-op
    }
};

const persistedTicketTabs = loadPersistedTicketTabs();

let initialState = {
    error: null,
    loading: false,
    globalLoader: false,
    sidebarCollapsed: false,
    statsCollapsed: false,
    ticketFiltersCollapsed: false,
    searchModalOpen: false,
    ticketCreateModalOpen: false,
    ticketCreatedAt: 0,
    menu: [],
    visibleCustomers: [],
    visibleProjects: [],
    visibleTickets: [],
    visibleTicketFilters: {
        searchField: 'code',
        searchTerm: '',
        statusFilter: 'ALL',
        statusGroupFilter: '',
        typeFilter: 'ALL',
        customerFilter: 'ALL',
        projectFilter: 'ALL',
    },
    visibleUsers: [],
    visibleRecords: [],
    openTicketTabs: persistedTicketTabs.openTicketTabs,
    activeTicketTabId: persistedTicketTabs.activeTicketTabId,
    ticketTabsCompanyId: persistedTicketTabs.companyId || '',
    ticketTabsUserId: persistedTicketTabs.userId || '',
}

export const uiReducer = ( state = initialState, action ) => {

    switch ( action.type ) {
        case types.setError:
            return {
                ...state,
                error: action.payload.error,
            }
        case types.unsetError:
            return {
                ...state,
                error: null,
            }
        case types.startLoading:
            return {
                ...state,
                loading: true,
            }
        case types.finishLoading:
            return {
                ...state,
                loading: false,
            }
        case types.startGlobalLoading:
            return {
                ...state,
                globalLoader: true,
            }
        case types.finishGlobalLoading:
            return {
                ...state,
                globalLoader: false,
            }
        case types.setMenu:
            return {
                ...state,
                menu: action.payload.menu,
            }
        case types.toggleSidebar:
            return {
                ...state,
                sidebarCollapsed: !state.sidebarCollapsed,
            }
        case types.toggleStats:
            return {
                ...state,
                statsCollapsed: !state.statsCollapsed,
            }
        case types.toggleTicketFilters:
            return {
                ...state,
                ticketFiltersCollapsed: !state.ticketFiltersCollapsed,
            }
        case types.openSearchModal:
            return {
                ...state,
                searchModalOpen: true,
            }
        case types.closeSearchModal:
            return {
                ...state,
                searchModalOpen: false,
            }
        case types.setVisibleTicketFilters:
            return {
                ...state,
                visibleTicketFilters: {
                    ...state.visibleTicketFilters,
                    ...(action.payload?.filters || {}),
                },
            }
        case types.openTicketCreateModal:
            return {
                ...state,
                ticketCreateModalOpen: true,
            }
        case types.login: {
            const nextUserId = String(action.payload?.id || '').trim();
            const isSameUser = Boolean(nextUserId) && nextUserId === String(state.ticketTabsUserId || '');

            if (isSameUser) {
                return state;
            }

            const nextState = {
                ...state,
                openTicketTabs: [],
                activeTicketTabId: '',
                searchModalOpen: false,
                ticketTabsUserId: nextUserId,
            };

            persistTicketTabs(
                nextState.openTicketTabs,
                nextState.activeTicketTabId,
                nextState.ticketTabsCompanyId,
                nextState.ticketTabsUserId,
            );

            return nextState;
        }
        case types.setCompany: {
            const nextCompanyId = String(action.payload?.id || '').trim();
            const isSameCompany = Boolean(nextCompanyId) && nextCompanyId === String(state.ticketTabsCompanyId || '');

            if (isSameCompany) {
                return state;
            }

            const nextState = {
                ...state,
                openTicketTabs: [],
                activeTicketTabId: '',
                searchModalOpen: false,
                ticketTabsCompanyId: nextCompanyId,
            };

            persistTicketTabs(
                nextState.openTicketTabs,
                nextState.activeTicketTabId,
                nextState.ticketTabsCompanyId,
                nextState.ticketTabsUserId,
            );

            return nextState;
        }
        case types.unsetCompany:
        case types.logout: {
            const nextState = {
                ...state,
                openTicketTabs: [],
                activeTicketTabId: '',
                searchModalOpen: false,
                ticketTabsCompanyId: '',
                ticketTabsUserId: '',
            };

            persistTicketTabs(
                nextState.openTicketTabs,
                nextState.activeTicketTabId,
                nextState.ticketTabsCompanyId,
                nextState.ticketTabsUserId,
            );

            return nextState;
        }
        case types.closeTicketCreateModal:
            return {
                ...state,
                ticketCreateModalOpen: false,
            }
        case types.notifyTicketCreated:
            return {
                ...state,
                ticketCreatedAt: Number(action.payload?.timestamp || Date.now()),
            }
        case types.setVisibleCustomers:
            return {
                ...state,
                visibleCustomers: action.payload.customers,
            }
        case types.setVisibleProjects:
            return {
                ...state,
                visibleProjects: action.payload.projects,
            }
        case types.setVisibleTickets:
            return {
                ...state,
                visibleTickets: action.payload.tickets,
            }
        case types.setVisibleUsers:
            return {
                ...state,
                visibleUsers: action.payload.users,
            }
        case types.setVisibleRecords:
            return {
                ...state,
                visibleRecords: action.payload.records,
            }
        case types.openOrFocusTicketTab: {
            const rawTicket = action.payload?.ticket || {};
            const ticketId = String(rawTicket.id || rawTicket._id || '').trim();

            if (!ticketId) {
                return state;
            }

            const nextTab = {
                id: ticketId,
                code: String(rawTicket.code || '').trim(),
                title: String(rawTicket.title || '').trim(),
            };

            const existingIndex = state.openTicketTabs.findIndex((tab) => String(tab?.id) === ticketId);
            const nextOpenTabs = existingIndex >= 0
                ? state.openTicketTabs.map((tab, index) => (index === existingIndex ? { ...tab, ...nextTab } : tab))
                : [...state.openTicketTabs, nextTab];

            const nextState = {
                ...state,
                openTicketTabs: nextOpenTabs,
                activeTicketTabId: ticketId,
            };

            persistTicketTabs(
                nextState.openTicketTabs,
                nextState.activeTicketTabId,
                state.ticketTabsCompanyId,
                state.ticketTabsUserId,
            );

            return nextState;
        }
        case types.closeTicketTab: {
            const closingId = String(action.payload?.ticketId || '').trim();
            if (!closingId) {
                return state;
            }

            const currentTabs = Array.isArray(state.openTicketTabs) ? state.openTicketTabs : [];
            const closingIndex = currentTabs.findIndex((tab) => String(tab?.id) === closingId);
            if (closingIndex < 0) {
                return state;
            }

            const nextOpenTabs = currentTabs.filter((tab) => String(tab?.id) !== closingId);
            const isClosingActive = String(state.activeTicketTabId || '') === closingId;

            let nextActiveId = state.activeTicketTabId;
            if (isClosingActive) {
                if (nextOpenTabs.length === 0) {
                    nextActiveId = '';
                } else {
                    const nextIndex = Math.min(closingIndex, nextOpenTabs.length - 1);
                    nextActiveId = String(nextOpenTabs[nextIndex]?.id || '');
                }
            }

            const nextState = {
                ...state,
                openTicketTabs: nextOpenTabs,
                activeTicketTabId: nextActiveId,
            };

            persistTicketTabs(
                nextState.openTicketTabs,
                nextState.activeTicketTabId,
                state.ticketTabsCompanyId,
                state.ticketTabsUserId,
            );

            return nextState;
        }
        case types.setActiveTicketTab: {
            const nextActiveId = String(action.payload?.ticketId || '').trim();

            if (!nextActiveId) {
                const nextState = {
                    ...state,
                    activeTicketTabId: '',
                };

                persistTicketTabs(
                    nextState.openTicketTabs,
                    nextState.activeTicketTabId,
                    state.ticketTabsCompanyId,
                    state.ticketTabsUserId,
                );

                return nextState;
            }

            const exists = state.openTicketTabs.some((tab) => String(tab?.id) === nextActiveId);
            if (!exists) {
                return state;
            }

            const nextState = {
                ...state,
                activeTicketTabId: nextActiveId,
            };

            persistTicketTabs(
                nextState.openTicketTabs,
                nextState.activeTicketTabId,
                state.ticketTabsCompanyId,
                state.ticketTabsUserId,
            );

            return nextState;
        }
        case types.clearTicketTabs: {
            const nextState = {
                ...state,
                openTicketTabs: [],
                activeTicketTabId: '',
            };

            persistTicketTabs(
                nextState.openTicketTabs,
                nextState.activeTicketTabId,
                state.ticketTabsCompanyId,
                state.ticketTabsUserId,
            );

            return nextState;
        }
        default:
            return state;
    }

}